<script setup>
// ACESSO-01 (L9b): o papel de uma pessoa, com a chave ACESSO_GRANULAR ligada, num select só, no convite e na edição. Ele
// toma o lugar do papel base e do papel personalizado de hoje: as opções são os modelos da Powerbizz, do catálogo, e os
// papéis da conta.
//
// - O valor é `modelo:<chave>` ou `papel:<id>` (`acessoDaPessoa`).
// - Os papéis da conta pedem o `roles:manage`: sem ele, ficam só os modelos.
// - Ninguém dá o que não tem (R6): o modelo que traz um escopo que falta a quem atribui não aparece, e o Administrador,
//   que traz o `roles:manage`, só aparece para quem o tem. O papel atual da pessoa aparece sempre, para o select dizer o
//   que ela tem.
// - Abaixo do select, em cinza, o que o papel escolhido faz: a frase do modelo no catálogo, ou a descrição do papel da
//   conta. O nome sozinho não dizia nada a quem escolhe (revisão de desenho, Task 9.6), e a exceção já fazia o mesmo
//   com a frase do escopo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAcesso } from 'dashboard/composables/useAcesso';
import AcessoAPI from 'dashboard/api/acesso';
import Select from 'dashboard/components-next/select/Select.vue';
import { valorDoPapel } from './acessoDaPessoa';

const props = defineProps({
  // O papel de hoje, na edição (`agent.papel`).
  atual: { type: Object, default: null },
});

// O `aria-labelledby` de quem usa vai para o <select>, e não para a caixa em volta dele.
defineOptions({ inheritAttrs: false });

const valor = defineModel({ type: String, default: '' });

const { t } = useI18n();
const store = useStore();
const { pode } = useAcesso();
const catalogo = useMapGetter('acesso/catalogo');
const papeisDaConta = ref([]);

const valorAtual = computed(() => props.atual && valorDoPapel(props.atual));

// Os escopos de um modelo: os do grupo da Powerbizz que ele escolhe em cada módulo.
const escoposDoModelo = modelo =>
  Object.entries(modelo.grupos).flatMap(
    ([modulo, chave]) =>
      catalogo.value.grupos.find(
        grupo =>
          grupo.origem === 'powerbizz' &&
          grupo.modulo === modulo &&
          grupo.chave === chave
      )?.escopos || []
  );
const podeDar = modelo => escoposDoModelo(modelo).every(escopo => pode(escopo));

const modelos = computed(() =>
  (catalogo.value?.modelos || [])
    .filter(
      modelo => `modelo:${modelo.chave}` === valorAtual.value || podeDar(modelo)
    )
    .map(modelo => ({ value: `modelo:${modelo.chave}`, label: modelo.rotulo }))
);

const daConta = computed(() => {
  const opcoes = papeisDaConta.value.map(papel => ({
    value: `papel:${papel.id}`,
    label: papel.nome,
  }));
  const faltaOAtual =
    props.atual?.tipo === 'papel' &&
    !opcoes.some(opcao => opcao.value === valorAtual.value);
  return faltaOAtual
    ? [{ value: valorAtual.value, label: props.atual.nome }, ...opcoes]
    : opcoes;
});

const fraseDoEscolhido = computed(() => {
  const [tipo, chave] = valor.value.split(':');
  if (tipo === 'modelo') {
    return catalogo.value?.modelos?.find(modelo => modelo.chave === chave)
      ?.frase;
  }
  return papeisDaConta.value.find(papel => String(papel.id) === chave)
    ?.descricao;
});

const grupos = computed(() =>
  [
    { label: t('AGENT_MGMT.ACESSO.PAPEL.MODELOS'), options: modelos.value },
    { label: t('AGENT_MGMT.ACESSO.PAPEL.DA_CONTA'), options: daConta.value },
  ].filter(grupo => grupo.options.length)
);

onMounted(async () => {
  // Sem o catálogo, o select fica sem os modelos.
  store.dispatch('acesso/carregarCatalogo').catch(() => {});
  if (!pode('roles:manage')) return;
  try {
    const { data } = await AcessoAPI.papeis();
    papeisDaConta.value = data.papeis.filter(papel => papel.origem === 'conta');
  } catch {
    // Sem a lista, ficam os modelos.
  }
});
</script>

<template>
  <div class="flex flex-col gap-1">
    <Select v-model="valor" :groups="grupos" class="!w-full" v-bind="$attrs" />
    <p
      v-if="fraseDoEscolhido"
      class="mb-0 text-xs text-n-slate-11"
      data-frase-do-papel
    >
      {{ fraseDoEscolhido }}
    </p>
  </div>
</template>
